// I metadati come DATO: si leggono dal testo, si riscrivono nel testo.
//
// Funzioni pure — testo dentro, testo fuori — perché la finestra dei metadati
// non deve sapere come è fatto un documento, e il documento non deve sapere
// che esiste una finestra.

export interface Meta
{
    chiave: string;
    valore: string;
}


const DIRETTIVA = /<\?\s*META:(\w+)\s+(?:"([^"]*)"|([^\s?][^?]*?))\s*\?>/;
const FENCE = /^\s*(`{3,}|~{3,})/;


// Una riga che è SOLO una direttiva: quelle si possono riscrivere e cancellare
// senza toccare prosa. Un META in mezzo a una frase resta dov'è — la finestra
// lo mostra ma non lo sposta.
function soloDirettiva(riga: string): boolean
{
    return /^\s*<\?\s*META:[^?]*\?>\s*$/.test(riga);
}


export function leggiMeta(testo: string): Meta[]
{
    const fuori: Meta[] = [];
    let recinto: string | null = null;

    for (const riga of testo.split("\n"))
    {
        const apertura = FENCE.exec(riga);
        if (apertura)
        {
            recinto = recinto === null ? (apertura[1] ?? "```") : null;
            continue;
        }
        if (recinto !== null) { continue; }

        // Dentro i backtick un META è un esempio, non una direttiva.
        const senzaLiterali = riga.replace(/`+[^`]*`+/g, "");
        const m = DIRETTIVA.exec(senzaLiterali);
        if (m) { fuori.push({ chiave: m[1] ?? "", valore: m[2] ?? m[3] ?? "" }); }
    }

    return fuori;
}


function scrivi(m: Meta): string
{
    // Virgolette SEMPRE, anche su una parola sola. Il valore che ne ha bisogno
    // (uno spazio, un `?`) e quello che non ne ha si scrivono nello stesso
    // modo: così le direttive del documento sono tutte uguali a vedersi, e
    // aggiungere una parola a un valore non cambia anche la forma della riga.
    return `<? META:${m.chiave} "${m.valore.replace(/"/g, "'")}" ?>`;
}


/**
 * Riscrive il documento con esattamente questi metadati.
 *
 * Chi c'era resta DOVE era (una direttiva in mezzo al testo non si sposta in
 * cima); chi è sparito se ne va con la sua riga; chi è nuovo si aggiunge dopo
 * l'ultima direttiva esistente, o in testa se non ce n'erano.
 */
export function applicaMeta(testo: string, nuovi: Meta[]): string
{
    const restano = new Map(nuovi.map((m) => [m.chiave, m]));
    const viste = new Set<string>();
    const righe: string[] = [];
    let ultimaMeta = -1;
    let recinto: string | null = null;

    for (const riga of testo.split("\n"))
    {
        const apertura = FENCE.exec(riga);
        if (apertura) { recinto = recinto === null ? (apertura[1] ?? "```") : null; righe.push(riga); continue; }
        if (recinto !== null) { righe.push(riga); continue; }

        const m = DIRETTIVA.exec(riga.replace(/`+[^`]*`+/g, ""));
        if (!m) { righe.push(riga); continue; }

        const chiave = m[1] ?? "";
        const aggiornato = restano.get(chiave);

        if (aggiornato === undefined)
        {
            // Sparito: via la riga, se la riga era solo sua. Altrimenti resta
            // la prosa e sparisce la sola direttiva.
            if (soloDirettiva(riga)) { continue; }
            righe.push(riga.replace(DIRETTIVA, "").trimEnd());
            continue;
        }

        viste.add(chiave);
        righe.push(riga.replace(DIRETTIVA, scrivi(aggiornato)));
        ultimaMeta = righe.length - 1;
    }

    const nuoveVoci = nuovi.filter((m) => !viste.has(m.chiave)).map(scrivi);
    if (nuoveVoci.length > 0)
    {
        if (ultimaMeta >= 0) { righe.splice(ultimaMeta + 1, 0, ...nuoveVoci); }
        else { righe.unshift(...nuoveVoci, ""); }
    }

    return righe.join("\n");
}
